import { useMemo, useRef, useEffect, useState } from "react";
import { useTableOfContents } from "../tableOfContentsHooks/useTableOfContents";

/**
 * Transforms the existing TOC structure into a format suitable for the DrugCategoriesIndex component.
 */
export const useDrugCategoriesIndex = () => {
  const tocContext = useTableOfContents();
  const hasUserInteracted = useRef(false);
  const [manuallyActiveCategory, setManuallyActiveCategory] = useState(null);

  const drugCategories = useMemo(() => {
    if (!tocContext.tableOfContents?.children) {
      return [];
    }

    // Extract drug category items from TOC
    const categoryItems = tocContext.tableOfContents.children.filter((item) => {
      return (
        item.title &&
        item.title.includes("Drugs and their categories in pregnancy and breastfeeding:")
      );
    });

    // Transform TOC items into category objects
    const categories = categoryItems.map((item) => {
      // Extract the letter from the title (e.g., "A", "B", "C")
      const match = item.title.match(/:\s*([A-Z])$/);
      const letter = match ? match[1] : "";

      return {
        letter,
        isAvailable: true,
        title: item.title,
        href: item.href,
        msPath: item.msPath,
        sectionId: item.id,
        tocItem: item, // Keep reference to original TOC item
      };
    });

    categories.sort((a, b) => a.letter.localeCompare(b.letter));

    return categories;
  }, [tocContext.tableOfContents]);

  // Listen for manual toggle events from the collapsible component
  useEffect(() => {
    const handleManualToggle = (event) => {
      if (event.detail.isManualToggle) {
        hasUserInteracted.current = true;
        setManuallyActiveCategory(event.detail.letter);

        // Handle clearing the active category when letter is null
        if (event.detail.letter === null) {
          // Clear the TOC active item when no section is active
          tocContext.setActiveItem(null);
        } else {
          // Also update the TOC context to sync with the sidebar
          const categoryItem = drugCategories.find((cat) => cat.letter === event.detail.letter);
          if (categoryItem && categoryItem.tocItem) {
            tocContext.setActiveItem(categoryItem.tocItem);
          }
        }
      }
    };

    window.addEventListener("drugCategorySelected", handleManualToggle);
    return () => {
      window.removeEventListener("drugCategorySelected", handleManualToggle);
    };
  }, [drugCategories, tocContext]);

  const navigateToCategory = (category) => {
    // Mark that user has interacted with the navigation
    hasUserInteracted.current = true;

    // Clear manual category since user clicked A-Z button
    setManuallyActiveCategory(null);

    if (category.tocItem) {
      tocContext.setSelectedMenuItem(category.tocItem);
      tocContext.setActiveItem(category.tocItem);
    }

    // Emit a custom event for the main content to listen to
    const event = new CustomEvent("drugCategorySelected", {
      detail: { letter: category.letter, category },
    });
    window.dispatchEvent(event);
  };

  const getActiveCategory = () => {
    // Don't show any active category until user has interacted
    if (!hasUserInteracted.current) {
      return null;
    }

    // Prioritise manually toggled category
    if (manuallyActiveCategory) {
      return manuallyActiveCategory;
    }

    const activeItem = tocContext.activeItem;
    if (activeItem && activeItem.title) {
      const match = activeItem.title.match(/:\s*([A-Z])$/);
      if (match) {
        // Only return active category if it's a drug category item
        const isDrugCategoryItem = activeItem.title.includes(
          "Drugs and their categories in pregnancy and breastfeeding:",
        );
        return isDrugCategoryItem ? match[1] : null;
      }
    }
    return null;
  };

  const allLetters = useMemo(() => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const availableLetters = new Set(drugCategories.map((cat) => cat.letter));

    return alphabet.map((letter) => {
      const categoryData = drugCategories.find((cat) => cat.letter === letter);
      return {
        letter,
        isAvailable: availableLetters.has(letter),
        ...categoryData,
      };
    });
  }, [drugCategories]);

  return {
    categories: allLetters,
    availableCategories: drugCategories,
    navigateToCategory,
    // eslint-disable-next-line
    activeCategory: getActiveCategory(),
    isLoading: !tocContext.tableOfContents,
  };
};
